import { createStore } from 'vuex'
import quizModule from './modules/quiz'
import leaderboardModule from './modules/leaderboard'
import starsModule from './modules/stars'

const imagesModule = {
  namespaced: true,
  state: {
    isOpen: false,
    placeName: '',
    itemSlug: '',
    images: [],
    currentImageIndex: 0,
    loading: false
  },
  mutations: {
    SET_MODAL_OPEN(state, isOpen) {
      state.isOpen = isOpen
    },
    SET_PLACE_NAME(state, placeName) {
      state.placeName = placeName
    },
    SET_ITEM_SLUG(state, itemSlug) {
      state.itemSlug = itemSlug
    },
    SET_IMAGES(state, images) {
      state.images = images
    },
    SET_CURRENT_IMAGE_INDEX(state, index) {
      state.currentImageIndex = index
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    }
  },
  actions: {
    async openModal({ commit, dispatch }, { placeName, country, itemSlug }) {
      commit('SET_PLACE_NAME', placeName)
      commit('SET_ITEM_SLUG', itemSlug || '')
      commit('SET_LOADING', true)
      commit('SET_MODAL_OPEN', true)
      commit('SET_CURRENT_IMAGE_INDEX', 0)
      
      const query = `${placeName} ${country}`
      await dispatch('fetchImages', { query, itemSlug })
    },
    closeModal({ commit }) {
      commit('SET_MODAL_OPEN', false)
      commit('SET_PLACE_NAME', '')
      commit('SET_ITEM_SLUG', '')
      commit('SET_IMAGES', [])
      commit('SET_CURRENT_IMAGE_INDEX', 0)
      commit('SET_LOADING', false)
    },
    async fetchImages({ commit }, { query, itemSlug }) {
      try {
        let url = `${process.env.VUE_APP_API_URL}/api/images?query=${encodeURIComponent(query)}`
        if (itemSlug) {
          url += `&itemSlug=${encodeURIComponent(itemSlug)}`
        }
        const response = await fetch(url)
        const data = await response.json()
        
        if (data && data.length > 0) {
          commit('SET_IMAGES', data)
        } else {
          commit('SET_IMAGES', [])
        }
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching images:', error)
        commit('SET_IMAGES', [])
        commit('SET_LOADING', false)
      }
    },
    setCurrentImage({ commit }, index) {
      commit('SET_CURRENT_IMAGE_INDEX', index)
      commit('SET_LOADING', true)
    },
    prevImage({ state, commit }) {
      const newIndex = state.currentImageIndex > 0 
        ? state.currentImageIndex - 1 
        : state.images.length - 1
      commit('SET_CURRENT_IMAGE_INDEX', newIndex)
      commit('SET_LOADING', true)
    },
    nextImage({ state, commit }) {
      const newIndex = state.currentImageIndex < state.images.length - 1
        ? state.currentImageIndex + 1
        : 0
      commit('SET_CURRENT_IMAGE_INDEX', newIndex)
      commit('SET_LOADING', true)
    },
    onImageLoad({ commit }) {
      commit('SET_LOADING', false)
    },
    onImageError({ commit }) {
      commit('SET_LOADING', false)
      console.error('Failed to load image.')
    }
  }
}

const moreInfoModule = {
  namespaced: true,
  state: {
    isOpen: false,
    placeName: '',
    title: '',
    introduction: '',
    body: '',
    images: [],
    html: '',
    query: '',
    slug: '',
    articleId: '',
    itemSlug: '',
    placeSlug: '',
    loading: false,
    error: null
  },
  mutations: {
    SET_MODAL_OPEN(state, isOpen) {
      state.isOpen = isOpen
    },
    SET_PLACE_NAME(state, placeName) {
      state.placeName = placeName
    },
    SET_ARTICLE_DATA(state, articleData) {
      state.title = articleData.title || ''
      state.introduction = articleData.introduction || ''
      state.body = articleData.body || ''
      state.images = articleData.images || []
      state.html = articleData.html || ''
      state.query = articleData.query || ''
      state.slug = articleData.slug || ''
      state.articleId = articleData._id || ''
      state.itemSlug = articleData.itemSlug || ''
      state.placeSlug = articleData.placeSlug || ''
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_CONTENT(state) {
      state.title = ''
      state.introduction = ''
      state.body = ''
      state.images = []
      state.html = ''
      state.query = ''
      state.slug = ''
      state.articleId = ''
      state.itemSlug = ''
      state.placeSlug = ''
    }
  },
  actions: {
    async openModal({ commit, dispatch }, { placeName, country, itemSlug = null, slug = null, placeSlug = null }) {
      commit('SET_PLACE_NAME', placeName)
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      commit('SET_MODAL_OPEN', true)
      
      const query = `${placeName} ${country}`
      await dispatch('fetchMoreInfo', { query, itemSlug, slug, placeSlug })
    },
    closeModal({ commit }) {
      commit('SET_MODAL_OPEN', false)
      commit('SET_PLACE_NAME', '')
      commit('CLEAR_CONTENT')
      commit('SET_LOADING', false)
      commit('SET_ERROR', null)
    },
    async fetchMoreInfo({ commit }, { query, itemSlug = null, slug = null, placeSlug = null }) {
      try {
        let url = `${process.env.VUE_APP_API_URL}/api/moreInfo`
        const params = new URLSearchParams()
        
        if (slug) {
          params.append('slug', slug)
        } else if (query) {
          params.append('query', query)
        }
        
        if (itemSlug) {
          params.append('itemSlug', itemSlug)
        }
        
        if (placeSlug) {
          params.append('placeSlug', placeSlug)
        }
        
        const response = await fetch(`${url}?${params.toString()}`)
        if (!response.ok) {
          throw new Error('Failed to fetch information')
        }
        
        const data = await response.json()
        
        if (data.title && data.body) {
          commit('SET_ARTICLE_DATA', data)
        } else {
          commit('SET_ERROR', 'No additional information available for this location.')
        }
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching more info:', error)
        commit('SET_ERROR', 'Unable to load additional information. Please try again later.')
        commit('SET_LOADING', false)
      }
    }
  }
}

const authModule = {
  namespaced: true,
  state: {
    user: null,
    token: localStorage.getItem('auth-token') || null,
    isLoading: false,
    error: null
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem('auth-token', token)
      } else {
        localStorage.removeItem('auth-token')
      }
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.error = null
      localStorage.removeItem('auth-token')
    }
  },
  actions: {
    async register({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userData)
        })
        
        const data = await response.json()
        
        if (!response.ok) {
          throw new Error(data.error || 'Registration failed')
        }
        
        commit('SET_TOKEN', data.token)
        commit('SET_USER', data.user)
        commit('SET_LOADING', false)
        
        return { success: true }
      } catch (error) {
        commit('SET_ERROR', error.message)
        commit('SET_LOADING', false)
        return { success: false, error: error.message }
      }
    },
    
    async login({ commit }, credentials) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(credentials)
        })
        
        const data = await response.json()
        
        if (!response.ok) {
          throw new Error(data.error || 'Login failed')
        }
        
        commit('SET_TOKEN', data.token)
        commit('SET_USER', data.user)
        commit('SET_LOADING', false)
        
        return { success: true }
      } catch (error) {
        commit('SET_ERROR', error.message)
        commit('SET_LOADING', false)
        return { success: false, error: error.message }
      }
    },
    
    async ssoLogin({ commit }, { provider, token }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/auth/sso`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, token })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'SSO login failed')
        }

        commit('SET_TOKEN', data.token)
        commit('SET_USER', data.user)
        commit('SET_LOADING', false)

        return { success: true }
      } catch (error) {
        commit('SET_ERROR', error.message)
        commit('SET_LOADING', false)
        return { success: false, error: error.message }
      }
    },

    async logout({ commit }) {
      commit('CLEAR_AUTH')
    },
    
    async fetchProfile({ commit, state }) {
      if (!state.token) return
      
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${state.token}`
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          commit('SET_USER', data.user)
        } else {
          commit('CLEAR_AUTH')
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
        commit('CLEAR_AUTH')
      }
    }
  },
  getters: {
    isAuthenticated: state => !!state.token,
    user: state => state.user,
    isLoading: state => state.isLoading,
    error: state => state.error
  }
}

const placesHistoryModule = {
  namespaced: true,
  state: {
    history: [],
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 50
    }
  },
  mutations: {
    SET_HISTORY(state, history) {
      state.history = history
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination
    },
    ADD_PLACE_TO_HISTORY(state, place) {
      // Add to beginning of history array or update existing
      const existingIndex = state.history.findIndex(p => p._id === place._id)
      if (existingIndex >= 0) {
        state.history.splice(existingIndex, 1)
      }
      state.history.unshift(place)
    },
    REMOVE_PLACE_FROM_HISTORY(state, placeId) {
      state.history = state.history.filter(p => p._id !== placeId)
    },
    CLEAR_HISTORY(state) {
      state.history = []
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50
      }
    }
  },
  actions: {
    async fetchHistory({ commit, rootGetters }, { page = 1, limit = 50 } = {}) {
      if (!rootGetters['auth/isAuthenticated']) {
        commit('SET_ERROR', 'User not authenticated')
        return
      }

      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/place-history?page=${page}&limit=${limit}`, {
          headers: {
            'Authorization': `Bearer ${rootGetters['auth/user']?.token || localStorage.getItem('auth-token')}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch place history')
        }

        const data = await response.json()
        commit('SET_HISTORY', data.history)
        commit('SET_PAGINATION', data.pagination)
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error fetching place history:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async removePlace({ commit, rootGetters }, placeId) {
      if (!rootGetters['auth/isAuthenticated']) {
        commit('SET_ERROR', 'User not authenticated')
        return
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/place-history/${placeId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${rootGetters['auth/user']?.token || localStorage.getItem('auth-token')}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to remove place from history')
        }

        commit('REMOVE_PLACE_FROM_HISTORY', placeId)
        return { success: true }
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error removing place from history:', error)
        return { success: false, error: error.message }
      }
    },

    async clearHistory({ commit, rootGetters }) {
      if (!rootGetters['auth/isAuthenticated']) {
        commit('SET_ERROR', 'User not authenticated')
        return
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/place-history`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${rootGetters['auth/user']?.token || localStorage.getItem('auth-token')}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to clear place history')
        }

        commit('CLEAR_HISTORY')
        return { success: true }
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error clearing place history:', error)
        return { success: false, error: error.message }
      }
    },

    async fetchTopPlaces({ commit }, { limit = 20 } = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/place-history/top?limit=${limit}`)

        if (!response.ok) {
          throw new Error('Failed to fetch top visited places')
        }

        const data = await response.json()
        commit('SET_HISTORY', data.places)
        // Set default pagination for global places
        commit('SET_PAGINATION', {
          currentPage: 1,
          totalPages: 1,
          totalItems: data.places.length,
          itemsPerPage: limit
        })
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error fetching top places:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },
  getters: {
    history: state => state.history,
    loading: state => state.loading,
    error: state => state.error,
    pagination: state => state.pagination,
    hasHistory: state => state.history.length > 0
  }
}

export default createStore({
  state: {
    quizCountryName: '',
    activeTab: 'info',
    sidebarOpen: true,
    infoData: null,
    loading: false
  },
  mutations: {
    SET_QUIZ_COUNTRY_NAME(state, name) {
      state.quizCountryName = name
    },
    SET_ACTIVE_TAB(state, tab) {
      state.activeTab = tab
    },
    SET_SIDEBAR_OPEN(state, isOpen) {
      state.sidebarOpen = isOpen
    },
    SET_INFO_DATA(state, data) {
      state.infoData = data
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading
    }
  },
  actions: {
    updateQuizCountryName({ commit }, name) {
      commit('SET_QUIZ_COUNTRY_NAME', name)
    },
    updateActiveTab({ commit }, tab) {
      commit('SET_ACTIVE_TAB', tab)
    },
    toggleSidebar({ commit }, isOpen) {
      commit('SET_SIDEBAR_OPEN', isOpen)
    },
    updateInfo({ commit }, data) {
      commit('SET_INFO_DATA', data)
    },
    setLoading({ commit }, isLoading) {
      commit('SET_LOADING', isLoading)
    }
  },
  getters: {
    quizCountryName: state => state.quizCountryName,
    activeTab: state => state.activeTab,
    sidebarOpen: state => state.sidebarOpen,
    infoData: state => state.infoData,
    loading: state => state.loading
  },
  modules: {
    images: imagesModule,
    moreInfo: moreInfoModule,
    auth: authModule,
    placesHistory: placesHistoryModule,
    quiz: quizModule,
    leaderboard: leaderboardModule,
    stars: starsModule
  }
})